/*============================================================================
  product-card-interactions.js
  ---------------------------------------------------------------------------
  ONE reusable behaviour for the shared product card
  (snippets/product-grid-item.liquid). Because every listener is delegated on
  `document`, it automatically covers the card wherever it renders — collection
  pages, homepage featured collections (Bestsellers, Statement socks), product
  recommendations, hotspots, predictive search — and cards injected later by
  AJAX (filtering, recommendations), with no per-section init.

  What it adds — desktop image-hover behaviour is intentionally left untouched:

    • Tap / click a colour swatch  → the card's main image swaps to that colour
      WITHOUT opening another page, the swatch is clearly highlighted, and the
      card's product link is pointed at that variant so the PDP later opens on
      the chosen colour. (Desktop: this is the one added click behaviour; the
      existing hover-preview keeps working.)

    • Mobile: swipe the image left / right to view the SELECTED colour's 2nd
      image (never a different colour's).

  Performance / layout: no libraries, no per-card work on load, no layout shift
  — the primary <img> is swapped in place inside a fixed-ratio, object-fit:cover
  box, and the swipe uses `touch-action: pan-y` so vertical scrolling stays
  native and is never blocked.
==============================================================================*/
(function () {
  'use strict';

  var SWATCH = '.color-swatch--with-image';
  var CARD = '.grid-product';
  // The primary image lives in the ratio box / image-wrap. The 2nd image sits in
  // .grid-product__secondary-image and colour overlays are <div>s, so this only
  // ever matches the main <img>.
  var PRIMARY_IMG = '.grid__image-ratio img, .image-wrap img';

  function closest(node, sel) {
    return node && node.closest ? node.closest(sel) : null;
  }

  /* ---- Colour swatch → pin the image (desktop click + mobile tap) ---------- */
  function pinColor(swatch) {
    var card = closest(swatch, CARD);
    if (!card) return;

    var src = swatch.getAttribute('data-variant-image');
    var src2 = swatch.getAttribute('data-variant-image-2');
    var url = swatch.getAttribute('data-url');

    // Swap the actual primary image. No overlay is introduced, so there is
    // nothing to fight with the desktop hover layer, and a fixed-ratio +
    // object-fit:cover box means zero layout shift.
    var mask = card.querySelector('.grid-product__image-mask');
    var img = mask ? mask.querySelector(PRIMARY_IMG) : null;
    if (img && src) {
      img.setAttribute('srcset', '');   // drop the responsive set so `src` wins
      img.removeAttribute('sizes');
      if (img.getAttribute('src') !== src) img.setAttribute('src', src);
    }

    // Point the 2nd image (swipe on mobile, hover on desktop) at THIS colour's
    // own 2nd image so it never falls back to a different colour. If the colour
    // has no distinct 2nd image, disable the swipe for this state instead.
    var secImg = mask ? mask.querySelector('.grid-product__secondary-image img') : null;
    if (secImg) {
      if (src2) {
        secImg.setAttribute('srcset', '');
        secImg.removeAttribute('sizes');
        if (secImg.getAttribute('src') !== src2) secImg.setAttribute('src', src2);
        card.classList.remove('pci-no-second');
      } else {
        card.classList.add('pci-no-second');
      }
    }

    // Clear any swiped-to-2nd-image state so the chosen colour is what shows.
    card.classList.remove('pci-show-secondary');

    // Highlight — only within this card's own swatch group.
    var group = closest(swatch, '.grid-product__colors') || card;
    var siblings = group.querySelectorAll('.color-swatch');
    for (var i = 0; i < siblings.length; i++) {
      siblings[i].classList.remove('is-selected');
      siblings[i].removeAttribute('aria-current');
    }
    swatch.classList.add('is-selected');
    swatch.setAttribute('aria-current', 'true');

    // Retain the chosen variant when the customer opens the product page.
    if (url) {
      var link = card.querySelector('.grid-product__link');
      if (link) link.setAttribute('href', url);
    }
  }

  document.addEventListener('click', function (e) {
    var swatch = closest(e.target, SWATCH);
    if (!swatch || !closest(swatch, CARD)) return;
    // Stop the swatch (an <a>, or a <span> inside the card link) from navigating.
    e.preventDefault();
    pinColor(swatch);
  });

  // Keyboard parity: the swatch is a link, so Enter already fires `click` above.
  // Space on a link does nothing by default, so mirror it for keyboard users.
  document.addEventListener('keydown', function (e) {
    if (e.key !== ' ' && e.key !== 'Spacebar') return;
    var swatch = closest(e.target, SWATCH);
    if (!swatch || !closest(swatch, CARD)) return;
    e.preventDefault();
    pinColor(swatch);
  });

  /* ---- Mobile: swipe the image to the 2nd image ---------------------------- */
  // The card link overlay (.grid-product__link::after) is the touch target over
  // the image, so we bind on `document` and resolve the card from the touch
  // point. Non-passive move/end listeners are attached only for the lifetime of
  // a gesture that actually starts on a swipeable image.
  var THRESHOLD = 40;   // px of travel needed to commit a swipe
  var LOCK = 8;         // px before we decide horizontal vs vertical
  var g = null;         // active gesture state

  function onStart(e) {
    if (!e.touches || e.touches.length !== 1) return;
    var wrapper = closest(e.target, '.grid__item-image-wrapper');
    if (!wrapper) return;
    var card = closest(wrapper, CARD);
    // No swipe when there is no 2nd image, or the selected colour has none.
    if (!card || !card.querySelector('.grid-product__secondary-image') || card.classList.contains('pci-no-second')) return;

    g = {
      card: card,
      wrapper: wrapper,
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
      horizontal: false
    };
    wrapper.addEventListener('touchmove', onMove, { passive: false });
    wrapper.addEventListener('touchend', onEnd);
    wrapper.addEventListener('touchcancel', onEnd);
  }

  function onMove(e) {
    if (!g) return;
    var dx = e.touches[0].clientX - g.x;
    var dy = e.touches[0].clientY - g.y;

    if (!g.horizontal) {
      if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > LOCK) {
        cleanup();          // vertical intent → let the page scroll normally
        return;
      }
      if (Math.abs(dx) > LOCK && Math.abs(dx) >= Math.abs(dy)) {
        g.horizontal = true;
      }
    }
    if (g.horizontal && e.cancelable) {
      e.preventDefault();   // stop sideways rubber-banding + suppress the click
    }
  }

  function onEnd(e) {
    if (!g) return;
    if (g.horizontal) {
      var end = e.changedTouches ? e.changedTouches[0].clientX : g.x;
      var dx = end - g.x;
      if (Math.abs(dx) > THRESHOLD) {
        if (dx < 0) g.card.classList.add('pci-show-secondary');    // swipe left → 2nd image
        else g.card.classList.remove('pci-show-secondary');        // swipe right → back to primary
      }
      swallowClick(g.card);                 // a committed swipe must not open the PDP
      if (e.cancelable) e.preventDefault();
    }
    cleanup();
  }

  function swallowClick(card) {
    var kill = function (ev) {
      ev.preventDefault();
      ev.stopPropagation();
      card.removeEventListener('click', kill, true);
    };
    card.addEventListener('click', kill, true);
    setTimeout(function () { card.removeEventListener('click', kill, true); }, 350);
  }

  function cleanup() {
    if (g && g.wrapper) {
      g.wrapper.removeEventListener('touchmove', onMove, { passive: false });
      g.wrapper.removeEventListener('touchend', onEnd);
      g.wrapper.removeEventListener('touchcancel', onEnd);
    }
    g = null;
  }

  document.addEventListener('touchstart', onStart, { passive: true });
})();
